import { BankOutlined, UserOutlined } from '@ant-design/icons'
import { usePrefetchQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { dictionariesQueries } from '@/api/dictionaries/dictionariesQueries'
import AtomSwitch, {
  type AtomSwitchOption,
} from '@/components/Atoms/AtomSwitch/AtomSwitch'
import AuthLayout from '@/components/AuthLayout/AuthLayout'
import LegalForm from '@/components/Register/Forms/LegalForm/LegalForm'
import type { RegisterFormHandle } from '@/components/Register/Forms/lib/sharedFields'
import PhysicalForm from '@/components/Register/Forms/PhysicalForm/PhysicalForm'

import cn from './Register.module.scss'

type RegisterType = 'PHYSICAL' | 'LEGAL'

const REGISTER_TYPE_OPTIONS: AtomSwitchOption<RegisterType>[] = [
  { value: 'PHYSICAL', label: 'Физ. лицо', icon: <UserOutlined aria-hidden /> },
  { value: 'LEGAL', label: 'Юр. лицо', icon: <BankOutlined aria-hidden /> },
]

function Register() {
  const [registerType, setRegisterType] = useState<RegisterType>('PHYSICAL')
  usePrefetchQuery(dictionariesQueries.paymentTypes())
  usePrefetchQuery(dictionariesQueries.currencies())
  const physicalFormRef = useRef<RegisterFormHandle>(null)
  const legalFormRef = useRef<RegisterFormHandle>(null)

  const handleRegisterTypeChange = (nextType: RegisterType) => {
    if (nextType === registerType) return
    const [source, target] =
      nextType === 'LEGAL'
        ? [physicalFormRef.current, legalFormRef.current]
        : [legalFormRef.current, physicalFormRef.current]
    if (source && target) target.setSharedValues(source.getSharedValues())
    setRegisterType(nextType)
  }

  const navigate = useNavigate()
  const location = useLocation()
  const hasHistory = location.key !== 'default'

  const handleBack = () => {
    if (hasHistory) navigate(-1)
    else navigate('/')
  }

  return (
    <AuthLayout
      title="Регистрация"
      back={{
        label: hasHistory ? 'Назад' : 'На главную',
        onClick: handleBack,
      }}
    >
      <AtomSwitch
        options={REGISTER_TYPE_OPTIONS}
        value={registerType}
        onChange={handleRegisterTypeChange}
        ariaLabel="Тип регистрации"
      />

      <p className={cn.message}>
        <span className={cn.messageMark}>*</span> обязательные поля
      </p>

      <div className={cn.formPane} hidden={registerType !== 'PHYSICAL'}>
        <PhysicalForm ref={physicalFormRef} />
      </div>
      <div className={cn.formPane} hidden={registerType !== 'LEGAL'}>
        <LegalForm ref={legalFormRef} />
      </div>
    </AuthLayout>
  )
}

export default Register
